<script setup>
/**
 * Ürün & Stok Sorgulama Popup'ı
 * --------------------------------
 * Cevap kutusundaki ikon sırasından açılır. Temsilci ürünün adını veya
 * stok kodunu (4 hane vb.) yazar; mağazaya göre Shopify veya Ticimax'tan
 * CANLI veri çekilir, renk / beden / stok gösterilir. "Cevaba Yaz" deyince
 * hazır metin OTOMATİK GÖNDERİLMEZ — sadece cevap kutusuna yazılır, temsilci
 * düzenleyip kendisi gönderir.
 *
 * Sipariş alma panelindeki (panel-app) arama mantığı baz alınmıştır:
 *  - Shopify: /shopify-api/products.json (başlık + sku fallback), option1/2, inventory_quantity
 *  - Ticimax: /ticimax-api/urunler?q= + /ticimax-api/varyasyonlar?id= (secenekler, stok)
 */
/* eslint-disable no-use-before-define -- fonksiyon bildirimleri hoist edilir, çağrı sırası güvenli */
import { ref, computed, onMounted } from 'vue';

const emit = defineEmits(['insert', 'close']);

// Panel API anahtarı — nginx /shopify-api ve /ticimax-api proxy'leri bu başlığı
// ister. Anahtar zaten OrderManagementPanel.vue içinde de mevcut (yeni sır değil).
const PANEL_KEY = '69cc189637de1fe6455ab233504606758d2c9f3a06388e84';

function apiFetch(url) {
  return fetch(url, { headers: { 'X-Panel-Key': PANEL_KEY } });
}

// Ürün/stok sorgusu TÜM kanallarda daima Ticimax referans alır (kanaldan bağımsız).
const isTicimax = computed(() => true);
const platformLabel = computed(() => (isTicimax.value ? 'Ticimax' : 'Shopify'));

const searchInput = ref(null);
const query = ref('');
const searching = ref(false);
const results = ref([]);
const searchError = ref('');

// Seçili ürün detayı
const loadingDetail = ref(false);
const detailError = ref('');
const selectedProduct = ref(null); // { title }
// Normalize varyantlar: { color, colorLabel, size, stock(null=bilinmiyor), price, image }
const variants = ref([]);
const selectedColor = ref(null);

function fmtPrice(val) {
  if (val == null || val === '') return '';
  return parseFloat(val).toLocaleString('tr-TR', { minimumFractionDigits: 0 });
}

// stok null ise (Shopify takip etmiyorsa) "var" kabul ediyoruz
function isAvailable(v) {
  return v.stock == null || v.stock > 0;
}

// ---- Arama ----
let searchTimer = null;
function onInput() {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    if (query.value.trim().length >= 2) runSearch();
    else results.value = [];
  }, 300);
}

async function runSearch() {
  const q = query.value.trim();
  if (!q) return;
  searching.value = true;
  searchError.value = '';
  results.value = [];
  try {
    results.value = isTicimax.value
      ? await searchTicimax(q)
      : await searchShopify(q);
    if (!results.value.length) searchError.value = 'Ürün bulunamadı';
  } catch (e) {
    searchError.value = 'Arama hatası: ' + (e.message || e);
  } finally {
    searching.value = false;
  }
}

async function searchShopify(q) {
  const r = await apiFetch(
    '/shopify-api/products.json?title=' + encodeURIComponent(q) + '&limit=10'
  );
  const d = await r.json();
  let products = d.products || [];
  if (!products.length) {
    // başlıkta yoksa SKU (stok kodu) içinde ara
    const r2 = await apiFetch('/shopify-api/products.json?limit=250');
    const d2 = await r2.json();
    products = (d2.products || [])
      .filter(p =>
        (p.variants || []).some(
          v => v.sku && v.sku.toLowerCase().includes(q.toLowerCase())
        )
      )
      .slice(0, 10);
  }
  return products.map(p => ({
    id: p.id,
    title: p.title,
    price: p.variants?.[0]?.price,
    meta: (p.variants?.length || 0) + ' varyant',
    raw: p,
  }));
}

async function searchTicimax(q) {
  const r = await apiFetch('/ticimax-api/urunler?q=' + encodeURIComponent(q));
  const d = await r.json();
  return (d.products || []).map(p => ({
    id: p.urunKartiId || p.id,
    title: p.title,
    price: p.price,
    meta: 'Stok: ' + (p.stock ?? '-'),
    raw: p,
  }));
}

// ---- Ürün seçimi / varyant yükleme ----
async function selectProduct(item) {
  selectedProduct.value = { title: item.title };
  selectedColor.value = null;
  variants.value = [];
  detailError.value = '';
  results.value = [];
  loadingDetail.value = true;
  try {
    variants.value = isTicimax.value
      ? await loadTicimaxVariants(item.id, item.title)
      : normalizeShopify(item.raw);
    if (!variants.value.length)
      detailError.value = 'Varyant/stok bilgisi bulunamadı';
  } catch (e) {
    detailError.value = 'Detay alınamadı: ' + (e.message || e);
  } finally {
    loadingDetail.value = false;
  }
}

function normalizeShopify(p) {
  const opts = p.options || [];
  const colorKw = ['renk', 'color', 'colour', 'rng'];
  const sizeKw = ['beden', 'size', 'numara', 'ebat', 'ölçü'];
  let cIdx = -1;
  let sIdx = -1;
  opts.forEach((opt, idx) => {
    const n = (opt.name || '').toLowerCase();
    if (colorKw.some(k => n.includes(k))) cIdx = idx;
    else if (sizeKw.some(k => n.includes(k))) sIdx = idx;
  });
  return (p.variants || []).map(v => {
    const img = p.images?.find(im => im.variant_ids?.includes(v.id));
    return {
      color: cIdx >= 0 ? v['option' + (cIdx + 1)] : null,
      colorLabel: cIdx >= 0 ? v['option' + (cIdx + 1)] : null,
      size: sIdx >= 0 ? v['option' + (sIdx + 1)] : null,
      stock: v.inventory_quantity != null ? Number(v.inventory_quantity) : null,
      price: v.price,
      image: img?.src || p.image?.src || null,
    };
  });
}

async function loadTicimaxVariants(id) {
  const r = await apiFetch('/ticimax-api/varyasyonlar?id=' + id);
  const d = await r.json();
  return (d.variants || []).map(v => {
    const renk = (v.secenekler || []).find(s => s.tanim === 'Renk');
    const beden = (v.secenekler || []).find(
      s => s.tanim && s.tanim.includes('Beden')
    );
    return {
      color: renk ? renk.deger : null,
      colorLabel: renk ? renk.isim || renk.deger : null,
      size: beden ? beden.deger : null,
      stock: v.stok != null ? parseFloat(v.stok) : null,
      price: v.fiyat,
      image: v.resim || null,
    };
  });
}

// ---- Türetilen renk / beden listeleri ----
const colors = computed(() => {
  const map = new Map();
  variants.value.forEach(v => {
    if (v.color == null) return;
    if (!map.has(v.color)) {
      map.set(v.color, {
        value: v.color,
        label: v.colorLabel || v.color,
        image: v.image,
        inStock: false,
      });
    }
    if (isAvailable(v)) map.get(v.color).inStock = true;
  });
  // Sevgi istegi: stokta OLMAYAN renkler listelenmesin — sadece stogu olan renkler.
  return [...map.values()].filter(c => c.inStock);
});

const sizes = computed(() => {
  const map = new Map();
  variants.value.forEach(v => {
    if (v.size == null) return;
    if (selectedColor.value && v.color !== selectedColor.value) return;
    if (!map.has(v.size))
      map.set(v.size, { value: v.size, qty: 0, inStock: false });
    const entry = map.get(v.size);
    if (isAvailable(v)) {
      entry.inStock = true;
      if (v.stock != null) entry.qty += v.stock;
    }
  });
  return [...map.values()];
});

const hasColors = computed(() => colors.value.length > 0);
const hasSizes = computed(() => sizes.value.length > 0);

function toggleColor(c) {
  if (!c.inStock) return;
  selectedColor.value = selectedColor.value === c.value ? null : c.value;
}

function sizeLabel(s) {
  return s.inStock && s.qty ? `${s.value} · ${s.qty} adet` : s.value;
}

// ---- Hazır metin ----
const generatedText = computed(() => {
  if (!selectedProduct.value) return '';
  const t = selectedProduct.value.title;
  const lines = [t, ''];
  if (selectedColor.value) {
    const c = colors.value.find(x => x.value === selectedColor.value);
    const cName = c ? c.label : selectedColor.value;
    const availSizes = sizes.value.filter(s => s.inStock).map(s => s.value);
    if (availSizes.length) {
      lines.push(
        `${cName} renginde stokta olan bedenler: ${availSizes.join(', ')}.`
      );
      lines.push('', 'Hangi bedeni istersiniz? 🙂');
    } else if (hasSizes.value) {
      lines.push(`${cName} renginde şu an stokta beden bulunmuyor. 🙏`);
    } else {
      lines.push(`${cName} rengimiz stoklarımızda mevcut. 🙂`);
    }
  } else {
    const availColors = colors.value.filter(c => c.inStock).map(c => c.label);
    const availSizes = sizes.value.filter(s => s.inStock).map(s => s.value);
    if (availColors.length)
      lines.push('Mevcut renkler: ' + availColors.join(', '));
    if (availSizes.length)
      lines.push('Mevcut bedenler: ' + availSizes.join(', '));
    lines.push('', 'Hangi renk ve bedeni istersiniz? 🙂');
  }
  return lines.join('\n');
});

function insertToReply() {
  if (!generatedText.value) return;
  emit('insert', generatedText.value);
}

function backToSearch() {
  selectedProduct.value = null;
  variants.value = [];
  selectedColor.value = null;
}

function onKeydown(e) {
  if (e.key === 'Escape') emit('close');
}

onMounted(() => {
  searchInput.value?.focus();
});
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -- iç kullanım için Türkçe sabit metinler -->
<template>
  <div
    class="product-lookup absolute bottom-full left-0 mb-2 z-30 w-[340px] max-h-[440px] flex flex-col rounded-xl border border-n-weak bg-n-solid-1 shadow-xl overflow-hidden"
    @keydown="onKeydown"
  >
    <!-- Başlık -->
    <div
      class="flex items-center gap-2 px-3 py-2 border-b border-n-weak flex-shrink-0"
    >
      <span class="i-lucide-package size-4 text-n-slate-11" />
      <span class="text-sm font-medium text-n-slate-12 flex-1">
        Ürün & Stok Sorgula
      </span>
      <span
        class="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
        :class="
          isTicimax
            ? 'bg-amber-50 text-amber-700 border border-amber-200'
            : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
        "
      >
        {{ platformLabel }}
      </span>
      <button
        class="size-5 flex items-center justify-center text-n-slate-9 hover:text-n-slate-12"
        title="Kapat"
        @click="emit('close')"
      >
        <span class="i-lucide-x size-4" />
      </button>
    </div>

    <!-- Arama girişi (ürün detayı açık değilken) -->
    <div v-if="!selectedProduct" class="p-2 flex-shrink-0">
      <input
        ref="searchInput"
        v-model="query"
        type="text"
        placeholder="Ürün adı veya stok kodu (örn. 4 hane)…"
        class="w-full text-sm px-3 py-2 rounded-lg border border-n-weak bg-n-alpha-black1 text-n-slate-12 outline-none focus:border-n-brand"
        @input="onInput"
        @keydown.enter="runSearch"
      />
    </div>

    <!-- Sonuç listesi -->
    <div
      v-if="!selectedProduct"
      class="flex-1 overflow-y-auto px-2 pb-2 min-h-0"
    >
      <div v-if="searching" class="text-xs text-n-slate-9 px-1 py-2">
        Aranıyor…
      </div>
      <div v-else-if="searchError" class="text-xs text-n-slate-9 px-1 py-2">
        {{ searchError }}
      </div>
      <button
        v-for="item in results"
        :key="item.id"
        class="w-full text-left flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-n-alpha-2"
        @click="selectProduct(item)"
      >
        <div class="min-w-0 flex-1">
          <div class="text-sm text-n-slate-12 truncate">{{ item.title }}</div>
          <div class="text-[11px] text-n-slate-9">{{ item.meta }}</div>
        </div>
        <div
          v-if="item.price"
          class="text-xs font-semibold text-n-slate-11 flex-shrink-0"
        >
          {{ fmtPrice(item.price) }} ₺
        </div>
      </button>
    </div>

    <!-- Ürün detayı: renk / beden / stok -->
    <div v-else class="flex-1 overflow-y-auto px-3 py-2 min-h-0">
      <button
        class="flex items-center gap-1 text-xs text-n-slate-10 hover:text-n-slate-12 mb-2"
        @click="backToSearch"
      >
        <span class="i-lucide-arrow-left size-3.5" />
        Aramaya dön
      </button>

      <div class="text-sm font-semibold text-n-slate-12 mb-2">
        {{ selectedProduct.title }}
      </div>

      <div v-if="loadingDetail" class="text-xs text-n-slate-9 py-2">
        Renk / beden / stok yükleniyor…
      </div>
      <div v-else-if="detailError" class="text-xs text-red-600 py-2">
        {{ detailError }}
      </div>

      <template v-else>
        <!-- Renkler -->
        <div v-if="hasColors" class="mb-3">
          <div class="text-[11px] font-bold text-n-slate-9 uppercase mb-1.5">
            🎨 Renk
          </div>
          <div class="flex flex-wrap gap-1.5">
            <button
              v-for="c in colors"
              :key="c.value"
              class="px-2.5 py-1 rounded-lg border text-xs"
              :class="[
                !c.inStock
                  ? 'opacity-40 cursor-not-allowed border-n-weak text-n-slate-9 line-through'
                  : selectedColor === c.value
                    ? 'border-n-brand bg-n-brand/10 text-n-slate-12 font-medium'
                    : 'border-n-weak text-n-slate-11 hover:border-n-slate-8',
              ]"
              :disabled="!c.inStock"
              @click="toggleColor(c)"
            >
              {{ c.label }}{{ !c.inStock ? ' (Stok Yok)' : '' }}
            </button>
          </div>
        </div>

        <!-- Bedenler -->
        <div v-if="hasSizes" class="mb-3">
          <div class="text-[11px] font-bold text-n-slate-9 uppercase mb-1.5">
            📏 Beden{{ selectedColor ? ' · ' + selectedColor + ' rengi' : '' }}
          </div>
          <div class="flex flex-wrap gap-1.5">
            <span
              v-for="s in sizes"
              :key="s.value"
              class="px-2.5 py-1 rounded-lg border text-xs"
              :class="
                s.inStock
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                  : 'border-n-weak text-n-slate-9 line-through opacity-50'
              "
            >
              {{ sizeLabel(s) }}
            </span>
          </div>
        </div>

        <div v-if="!hasColors && !hasSizes" class="text-xs text-n-slate-9 py-1">
          Bu ürün için renk/beden seçeneği yok.
        </div>

        <!-- Hazır metin önizleme -->
        <div class="mt-2">
          <div class="text-[11px] font-bold text-n-slate-9 uppercase mb-1">
            Cevaba yazılacak metin
          </div>
          <!-- prettier-ignore -->
          <pre class="whitespace-pre-wrap text-xs text-n-slate-11 bg-n-alpha-black1 rounded-lg p-2 border border-n-weak font-sans">{{ generatedText }}</pre>
        </div>
      </template>
    </div>

    <!-- Alt aksiyon -->
    <div
      v-if="selectedProduct && !loadingDetail && !detailError"
      class="flex items-center gap-2 px-3 py-2 border-t border-n-weak flex-shrink-0"
    >
      <button
        class="flex-1 text-sm font-medium text-white bg-n-brand rounded-lg py-2 hover:opacity-90"
        @click="insertToReply"
      >
        Cevaba Yaz
      </button>
      <button
        class="text-sm text-n-slate-10 hover:text-n-slate-12 px-3 py-2"
        @click="emit('close')"
      >
        Kapat
      </button>
    </div>
  </div>
</template>
